// =============================================================================
// SIREPPAR - Formateo de fechas
// =============================================================================
// Hay DOS clases de fecha en el sistema y se formatean distinto. Confundirlas es
// lo que hace que un acto electoral del 13/12/2026 se muestre "12/12/2026".
//
//   1. FECHA DE CALENDARIO — un día del almanaque, sin hora propia.
//      Ejemplos: fecha del acto electoral, fecha de nacimiento, mandatos desde/hasta,
//      fecha de una resolución. Lo que se cargó en pantalla fue "2026-12-13" y eso
//      es lo que tiene que verse, sin importar el huso del navegador.
//      La base las guarda como DateTime a medianoche UTC, así que convertirlas a
//      hora argentina (UTC-3) las manda a las 21:00 del día ANTERIOR. Por eso acá
//      NO se aplica ningún huso: se leen los componentes tal cual vinieron.
//      Ojo: poner timeZone: 'America/Argentina/Buenos_Aires' NO arregla esto, lo
//      empeora — es justamente la conversión que resta el día.
//      -> formatFechaCalendario()
//
//   2. MARCA DE TIEMPO — un instante real que pasó (o vence) en un momento dado.
//      Ejemplos: creadoEn, fechaPresentacion, cierre de un plazo. Acá el huso SÍ
//      importa, porque el navegador puede estar en cualquier zona y el instante
//      tiene que leerse en hora de Chubut.
//      -> formatMarcaTiempo()
//
// Regla práctica: si el dato se cargó con un <input type="date">, es calendario.
// Si lo puso el sistema con new Date(), es marca de tiempo.
//
// Esta utilidad es gemela de la del backend (src/utils/fechas.ts allá): misma API
// y mismo criterio, para que la pantalla y el PDF nunca muestren días distintos.
// =============================================================================

export const TZ_ARG = 'America/Argentina/Buenos_Aires';

/** 'corta' -> 13/12/2026 · 'larga' -> 13 de diciembre de 2026 */
export type FormatoFecha = 'corta' | 'larga';

export type EntradaFecha = Date | string | null | undefined;

export interface OpcionesMarcaTiempo {
  formato?: FormatoFecha;
  /** Agrega la hora argentina. '24h' -> 15:40 · '12h' -> 03:40 p. m. */
  hora?: false | '24h' | '12h';
  /** Qué devolver cuando no hay fecha. */
  vacio?: string;
}

const dosDigitos = (n: number): string => String(n).padStart(2, '0');

/**
 * Descompone la entrada en año/mes/día de calendario SIN pasar por ningún huso.
 *
 * - String ISO ("2026-12-13" o "2026-12-13T00:00:00.000Z"): se corta la cadena.
 *   Es la única forma de no perder el día, porque `new Date("2026-12-13")` ya
 *   nace como medianoche UTC.
 * - Date: se leen los componentes UTC, que son los que conservan el día tal como
 *   se guardó.
 */
function partesCalendario(fecha: EntradaFecha): { anio: number; mes: number; dia: number } | null {
  if (fecha === null || fecha === undefined || fecha === '') return null;

  if (typeof fecha === 'string') {
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(fecha.trim());
    if (iso) return { anio: Number(iso[1]), mes: Number(iso[2]), dia: Number(iso[3]) };
    const parseada = new Date(fecha);
    if (isNaN(parseada.getTime())) return null;
    return {
      anio: parseada.getUTCFullYear(),
      mes: parseada.getUTCMonth() + 1,
      dia: parseada.getUTCDate(),
    };
  }

  if (isNaN(fecha.getTime())) return null;
  return { anio: fecha.getUTCFullYear(), mes: fecha.getUTCMonth() + 1, dia: fecha.getUTCDate() };
}

/**
 * Fecha de calendario: se muestra el mismo día que se cargó, sin corrimiento.
 *
 *   formatFechaCalendario('2026-12-13')                    -> '13/12/2026'
 *   formatFechaCalendario(convocatoria.fechaActo, 'larga')  -> '13 de diciembre de 2026'
 *   formatFechaCalendario(null)                             -> '—'
 *
 * NO usar para instantes reales (creadoEn, fechaPresentacion, cierres de plazo):
 * para eso está formatMarcaTiempo(), que sí convierte a hora argentina.
 */
export function formatFechaCalendario(
  fecha: EntradaFecha,
  formato: FormatoFecha = 'corta',
  vacio = '—'
): string {
  const p = partesCalendario(fecha);
  if (!p) return vacio;

  if (formato === 'corta') return `${dosDigitos(p.dia)}/${dosDigitos(p.mes)}/${p.anio}`;

  // Para el nombre del mes se rearma la fecha en UTC y se formatea EN UTC: así el
  // huso no vuelve a entrar por la ventana.
  return new Date(Date.UTC(p.anio, p.mes - 1, p.dia)).toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Marca de tiempo: un instante real, expresado en hora argentina.
 *
 *   formatMarcaTiempo(expediente.creadoEn)                  -> '13/12/2026'
 *   formatMarcaTiempo(convocatoria.cierreAlianzas)          -> '13/09/2026'
 *   formatMarcaTiempo(new Date(), { hora: '24h' })          -> '13/12/2026 09:05'
 *
 * NO usar para fechas de calendario (fechaActo, fechaNacimiento, mandatos): ahí el
 * huso resta un día. Para eso está formatFechaCalendario().
 *
 * ⚠️ Si le pasás una cadena SIN hora ("2026-09-13"), devuelve 12/09/2026: la cadena
 * se interpreta como medianoche UTC y la conversión a UTC-3 la manda al día
 * anterior. Eso no es un defecto de la función, es la señal de que ese dato era una
 * fecha de calendario y va por formatFechaCalendario(). Antes de usar esta función,
 * confirmá que el valor trae hora real: `cierreAlianzas`, por ejemplo, la trae
 * porque el backend le fija el fin del día en -03:00 antes de serializarlo.
 */
export function formatMarcaTiempo(fecha: EntradaFecha, opciones: OpcionesMarcaTiempo = {}): string {
  const { formato = 'corta', hora = false, vacio = '—' } = opciones;

  if (fecha === null || fecha === undefined || fecha === '') return vacio;
  const d = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (isNaN(d.getTime())) return vacio;

  const partes: Intl.DateTimeFormatOptions =
    formato === 'larga'
      ? { day: 'numeric', month: 'long', year: 'numeric' }
      : { day: '2-digit', month: '2-digit', year: 'numeric' };

  const texto = d.toLocaleDateString('es-AR', { ...partes, timeZone: TZ_ARG });
  if (!hora) return texto;

  return `${texto} ${d.toLocaleTimeString('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: hora === '12h',
    timeZone: TZ_ARG,
  })}`;
}
